// b13: väderstationen vid platsen: Ecowitt via platsens gateway, eller BTHome (Shellys WS90).
// Navet räknar om till SI (m/s, mm, hPa och °C), och molnet visar det uppmätta bredvid
// prognosen.
//
// Misstänkt is: stationerna saknar värme. När temperaturen är under gränsen (+2 °C) och vinden
// står på 0, eller riktningen inte ändras alls, i flera timmar (3) medan prognosen för platsen
// visar vind (minst 3 m/s i medel), är vindvärdena misstänkt is. Då visas prognosens vind, och
// värmemodellen använder prognosen. Gränserna ställs per plats (granser.ts i molnet).

const STRECK = ["N", "NO", "O", "SO", "S", "SV", "V", "NV"];

/** Väderstrecket som det blåser från, med åtta streck: 225° är SV. */
export function vaderstreck(grader: number): string {
  const i = Math.round((((grader % 360) + 360) % 360) / 45) % 8;
  return STRECK[i] as string;
}

export interface Isgranser {
  /** Temperaturen ute under så här många grader. */
  tempGrader: number;
  /** I så här många timmar. */
  timmar: number;
  /** Medan prognosen visar minst så här mycket vind i medel. */
  prognosMs: number;
}

export interface Tidvarde {
  tid: number;
  varde: number;
}

/** Vinden står på 0 när den är högst så här mycket. */
const STILLA_MS = 0.1;
/** Riktningen ändras inte alls när den håller sig inom en grad. */
const SAMMA_GRADER = 1;
/** Minst så här många värden för vinden och riktningen, och temperaturen, under tiden. */
const MINST_VARDEN = 6;
const TIMME = 3_600_000;

/**
 * Misstänkt is: sedan när vinden har stått på 0, eller riktningen inte har ändrats alls, medan
 * det har varit kallt och prognosen har visat vind. Null annars. Värdena ska vara i tidsordning.
 */
export function misstanktIs(
  m: { temp: readonly Tidvarde[]; vind: readonly Tidvarde[]; riktning: readonly Tidvarde[] },
  prognos: readonly { tid: number; vind: number | null }[],
  g: Isgranser,
  nu: number,
): { sedan: number; orsak: "vinden" | "riktningen" } | null {
  /** Från när det senaste värdet och bakåt uppfyller villkoret, utan uppehåll över en timme. */
  const sedan = (lista: readonly Tidvarde[], ok: (v: number, forsta: number) => boolean) => {
    const sista = lista.at(-1);
    if (!sista || nu - sista.tid > TIMME) return null;
    let i = lista.length - 1;
    while (i > 0) {
      const forra = lista[i - 1] as Tidvarde;
      if (!ok(forra.varde, sista.varde) || (lista[i] as Tidvarde).tid - forra.tid > TIMME) break;
      i--;
    }
    if (!ok((lista[i] as Tidvarde).varde, sista.varde)) return null;
    return { fran: (lista[i] as Tidvarde).tid, antal: lista.length - i };
  };
  const kallt = sedan(m.temp, (v) => v < g.tempGrader);
  if (!kallt || kallt.antal < MINST_VARDEN) return null;
  const stilla = sedan(m.vind, (v) => v <= STILLA_MS);
  const samma = sedan(m.riktning, (v, sista) => Math.abs(skillnad(v, sista)) <= SAMMA_GRADER);
  const kandidater = [
    stilla && { ...stilla, orsak: "vinden" as const },
    samma && { ...samma, orsak: "riktningen" as const },
  ].filter((x): x is { fran: number; antal: number; orsak: "vinden" | "riktningen" } => !!x);
  for (const k of kandidater.sort((a, b) => a.fran - b.fran)) {
    const fran = Math.max(k.fran, kallt.fran);
    if (k.antal < MINST_VARDEN || nu - fran < g.timmar * TIMME) continue;
    const vind = prognos
      .filter((p) => p.tid >= fran - TIMME && p.tid <= nu && p.vind !== null)
      .map((p) => p.vind as number);
    if (vind.length < 2) continue;
    if (vind.reduce((a, b) => a + b, 0) / vind.length < g.prognosMs) continue;
    return { sedan: fran, orsak: k.orsak };
  }
  return null;
}

/** Skillnaden mellan två riktningar, −180 till 180. */
function skillnad(a: number, b: number): number {
  return ((((a - b) % 360) + 540) % 360) - 180;
}
